import { SessionApiImpl } from "@adora/backend-api";
import * as webVitals from "web-vitals";
import { AdoraEventStorageImpl, AdoraSessionStorageImpl, } from "./adora-session-storage";
import { retrieveBootstrapCohorts } from "./bootstrap-cohorts";
import { debugLog } from "./debug";
import { EventHolderImpl } from "./event-holder";
import { EventSync } from "./event-sync";
import { Recorder } from "./recorder";
import { RRwebRecorderImpl } from "./rrweb-recorder";
import { WindowSessionStorage } from "./storage";
import { urlAccepted } from "./url-accepted";
const SAVE_INTERVAL_MS = 5000;
export function initializeRecording({ sessionType, }) {
    debugLog("initialize recording");
    const storage = new AdoraSessionStorageImpl(sessionType, new WindowSessionStorage());
    const session = storage.getSession();
    if (!session) {
        return;
    }
    const sessionApi = new SessionApiImpl(session.settings.backendUrl);
    const eventStorage = new AdoraEventStorageImpl(sessionType, new WindowSessionStorage());
    const savedSessionEvents = eventStorage.getEvents();
    eventStorage.removeEvents();
    const getHref = () => {
        return window.location.href;
    };
    const getLanguage = () => {
        return window.navigator.language;
    };
    const getDevicePixelRatio = () => {
        return window.devicePixelRatio;
    };
    const rrwebRecorder = new RRwebRecorderImpl();
    const recorder = new Recorder(session.limitedCanvasRecording ?? false, session.canvasRecordingEnabled ?? false, session.organization, rrwebRecorder);
    const eventHolder = new EventHolderImpl(session.organization, eventStorage, getHref, recorder, savedSessionEvents || []);
    const eventSync = new EventSync(window, storage, eventHolder, recorder, sessionApi, session.organization, {
        getHref,
        getLanguage,
        getDevicePixelRatio,
    });
    const startLoc = window.location.href;
    if (urlAccepted({
        urlStr: startLoc,
        pathConfig: session.organization.pathConfig,
    })) {
        debugLog(`starting recording...`);
        recorder.setRecording(true);
    }
    else {
        debugLog(`recording did not start as current URL is blocked`);
    }
    const bootstrappedCohorts = retrieveBootstrapCohorts(window, session.organization.cohortsBootstrapConfig);
    // If we're already recording then update the cohorts and user id with the latest data
    if (recorder.isRecording) {
        recorder.recordChangeCustomCohort({
            uid: session.settings.uid,
            userCohorts: session.settings.userCohorts
                ? {
                    ...bootstrappedCohorts,
                    ...session.settings.userCohorts,
                }
                : {},
        });
        storage.setSession(session);
    }
    webVitals.onCLS((e) => {
        if (recorder.isRecording) {
            recorder.recordCLS(e);
        }
    });
    webVitals.onFID((e) => {
        if (recorder.isRecording) {
            recorder.recordFID(e);
        }
    });
    webVitals.onLCP((e) => {
        if (recorder.isRecording) {
            recorder.recordLCP(e);
        }
    });
    const saveClosure = () => {
        eventSync.clearAndSaveEvents(eventHolder.getEvents());
    };
    let saveInterval = window.setInterval(saveClosure, SAVE_INTERVAL_MS);
    document.addEventListener("visibilitychange", () => {
        debugLog(`detected a visibility change to ${document.visibilityState}`);
        if (recorder.isRecording) {
            recorder.recordVisibilityChange(document.visibilityState);
        }
        if (document.visibilityState === "hidden") {
            if (saveInterval) {
                debugLog(`clearing the saveInterval`);
                // Page being navigated away from.
                clearInterval(saveInterval);
                saveInterval = undefined;
            }
            // Try save the events to session storage, in case `saveEvents` does not succeed.
            eventHolder.onVisibilityChange(false);
            return;
        }
        if (!saveInterval) {
            debugLog(`resuming the saveInterval`);
            // If we are reappearing, restart the save interval.
            // We do not need to restore the session storage as the event holder is still
            // correctly populated.
            saveInterval = window.setInterval(saveClosure, SAVE_INTERVAL_MS);
        }
    });
    // For SPAs, determine if a location changes based on any DOM mutations.
    let previousUrl = startLoc;
    const observer = new MutationObserver(function () {
        if (eventHolder && window.location.href !== previousUrl) {
            const accepted = urlAccepted({
                urlStr: window.location.href,
                pathConfig: session.organization.pathConfig,
            });
            debugLog(`url changed detected, status: ${!!accepted}`);
            recorder.setRecording(!!accepted);
            previousUrl = window.location.href;
        }
    });
    observer.observe(document, { subtree: true, childList: true });
    const urlChangeObserverDispose = () => {
        observer.disconnect();
    };
    return { recorder, eventSync, urlChangeObserverDispose };
}
